import {
  addDays,
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import { FILTER_RANGE_MAX_DAYS } from './displayWindow';
import type { DisplayWindow } from './displayWindow';

/**
 * The date filters that can drive the visible range, in precedence order.
 * Appointment Date leads because appointments are what the board draws.
 */
export const DATE_FILTERS = [
  { id: 'appointmentDate', label: 'Appointment Date' },
  { id: 'receivedDate', label: 'Received Date' },
  { id: 'noticeDate', label: 'Notice Date' },
  { id: 'lossDate', label: 'Loss Date' },
] as const;

/**
 * The calendar day a date-filter value names, as local midnight.
 *
 * A bare `yyyy-MM-dd` (how saved filters store the day) must not go through
 * `new Date()`: that parses it as UTC midnight, which is the previous day
 * anywhere west of Greenwich.
 */
export function toCalendarDay(value: unknown): Date | null {
  if (value instanceof Date) {
    return isValid(value) ? startOfDay(value) : null;
  }
  if (typeof value === 'string' && value !== '') {
    const parsed = parseISO(value);
    return isValid(parsed) ? startOfDay(parsed) : null;
  }
  return null;
}

type DayPair = { from: Date | null; to: Date | null };

function parseTuple(value: unknown): unknown {
  if (typeof value !== 'string' || !value.startsWith('[')) return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** The From / To days a filter value names, earlier day first. */
function readDayPair(value: unknown): DayPair {
  const tuple = parseTuple(value);
  if (!Array.isArray(tuple)) return { from: null, to: null };
  const from = toCalendarDay(tuple[0]);
  const to = toCalendarDay(tuple[1]);
  if (from && to && to < from) return { from: to, to: from };
  return { from, to };
}

/** The `yyyy-MM-dd` From / To a complete date-range value names, else null. */
export function completeDateRangeDays(
  value: unknown
): { from: string; to: string } | null {
  const { from, to } = readDayPair(value);
  if (!from || !to) return null;
  return {
    from: format(from, 'yyyy-MM-dd'),
    to: format(to, 'yyyy-MM-dd'),
  };
}

export type ActiveDateFilterRange = {
  label: string;
  /** From day at midnight to the midnight after the To day (exclusive). */
  window: DisplayWindow;
  spanDays: number;
  /** The filter spanned more than `FILTER_RANGE_MAX_DAYS`; only its start is shown. */
  limited: boolean;
};

export type DateFilterRangeResult = {
  range: ActiveDateFilterRange | null;
  /** Labels of date filters with only one end set; they are not applied. */
  incomplete: string[];
};

/**
 * The range the board should show for the task filters in context.
 *
 * One filter's From / To pair is used whole — ends are never mixed across
 * filters. A filter with only one end set is skipped and reported instead.
 */
export function resolveDateFilterRange(
  taskFilters: Record<string, unknown> | undefined
): DateFilterRangeResult {
  const incomplete: string[] = [];
  let range: ActiveDateFilterRange | null = null;

  for (const filter of DATE_FILTERS) {
    const { from, to } = readDayPair(taskFilters?.[filter.id]);
    if (from === null && to === null) continue;
    if (from === null || to === null) {
      incomplete.push(filter.label);
      continue;
    }
    if (range) continue;

    const fullSpan = differenceInCalendarDays(to, from) + 1;
    const spanDays = Math.min(fullSpan, FILTER_RANGE_MAX_DAYS);
    range = {
      label: filter.label,
      window: { startDate: from, endDate: addDays(from, spanDays) },
      spanDays,
      limited: fullSpan > FILTER_RANGE_MAX_DAYS,
    };
  }

  return { range, incomplete };
}

/** A value that changes exactly when the days a range covers change. */
export function dateFilterRangeKey(
  range: ActiveDateFilterRange | null
): string {
  if (!range) return '';
  return `${format(range.window.startDate, 'yyyy-MM-dd')}+${range.spanDays}`;
}
